import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Badge from '@/components/Typography/Badge';
import { Image } from 'expo-image';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useModalStore } from '@/store';

const createStyles = (colors, isXS) => StyleSheet.create({
  header: {
    flexDirection: isXS ? 'column' : 'row',
    gap: 15,
    marginBottom: 20,
    width: '100%',
  },
  primaryImageContainer: {
    position: 'relative',
    width: isXS ? 70 : 100,
    height: isXS ? 70 : 100,
  },
  primaryImage: {
    width: isXS ? 70 : 100,
    height: isXS ? 70 : 100,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  primaryImageView: {
    width: isXS ? 70 : 100,
    height: isXS ? 70 : 100,
    borderRadius: 12,
    backgroundColor: colors.solid_color_secondary,
    justifyContent: 'center',
    alignItems: 'center'
  },
  companyLogo: {
    width: isXS ? 24 : 30,
    height: isXS ? 24 : 30,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  infoSection: {
    flex: 1,
    paddingTop: 2,
  },
  title: {
    marginBottom: isXS ? 6 : 1,
  },
  nameContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 4,
  },
  name: {
    marginBottom: isXS ? 4 : 2,
  },
  description: {
    textAlign: 'left',
    lineHeight: 20,
    opacity: 0.7,
  },
  plusBadge: {
    marginTop: 5,
  },
});

const ChatPageHeader = ({ title, name, description, image, isAgent = false, showPlusBadge = true }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const styles = React.useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const onPressPlusBadge = React.useCallback(() => {
    openModal('PlusFeatureModal', { feature: 'bots' });
  }, [openModal]);

  return (
    <View style={styles.header}>
      <View style={styles.primaryImageContainer}>
        {!!image ? (
          <Image source={{ uri: image }} style={styles.primaryImage} contentFit="cover" />
        ) : (
          <View style={styles.primaryImageView}>
            <Icon name="cube" size={40} color={colors.text_color_secondary} />
          </View>
        )}
      </View>
      <View style={styles.infoSection}>
        {title && <Text style={styles.title} size='n' weight='normal' color='secondary'>{title}</Text>}
        <View style={styles.nameContainer}>
          <Text size={isXS ? 'xl' : 'xxl'} weight='bold' color='primary' style={styles.name}>
            {name}
          </Text>
          {showPlusBadge && (
            <Badge
              text="PLUS"
              onPress={onPressPlusBadge}
              icon="sparkles"
              size="small"
              isPlus
              style={styles.plusBadge}
            />
          )}
        </View>
        <Text size='n' color='secondary' style={styles.description}>{description}</Text>
      </View>
    </View>
  );
};

export default React.memo(ChatPageHeader); 